// ============================================================
// PlayRummikub — 루미큐브 게임 플레이 페이지  (SEQ-10214 S5c)
//   라우트(TD §3.1): /play/rummikub/:roomCode
//   TTR PlayTicket.tsx 패턴 100% 정합 (authState/roomCode/matchId props + hub start + unmount stop)
//   Konva Stage 3 Layer:
//     - BoardLayer  (RmkBoard)   — 보드 세트·타일·드래그앤드롭
//     - HandLayer   (RmkHand)    — 손패 (본인 face, 타인 장수만)
//     - UILayer     (Konva 직접) — 턴 타이머·플레이어 리스트
//   정보 비대칭 강제:
//     - hand (face) = Caller scope broadcast에서만 → 본인만 보임
//     - handSizes   = Group broadcast HandSizeUpdated 장수만
// ============================================================

import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Stage as _Stage,
  Layer,
  Rect,
  Text,
  Circle,
} from 'react-konva'
import type Konva from 'konva'
import { AuthState } from '../store/auth'
import {
  rummikubStore,
  subscribeRummikub,
} from '../store/rummikub'
import * as rmkHub from '../hub/rummikubHub'
import RmkBoard       from '../components/RmkBoard'
import RmkHand        from '../components/RmkHand'
import RmkActionPanel from '../components/RmkActionPanel'
import RmkGameEndModal from '../components/RmkGameEndModal'

// react-konva 18 children FC workaround (CatanBoard / TtrBoard 패턴 동일)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Stage = _Stage as any

// ── 반응형 Stage 크기 ─────────────────────────────────────
function useWindowSize(): { width: number; height: number } {
  const [size, setSize] = useState({
    width:  typeof window !== 'undefined' ? window.innerWidth  : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  })
  useEffect(() => {
    function onResize() {
      setSize({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return size
}

// ── Props (TTR PlayTicket 정합) ───────────────────────────
export interface PlayRummikubProps {
  authState:  AuthState
  roomCode:   string
  matchId?:   number
  onLeave:    () => void
  onLogout:   () => void
}

const PlayRummikub: React.FC<PlayRummikubProps> = ({
  authState,
  roomCode,
  matchId,
  onLeave,
  onLogout,
}) => {
  // ── store 구독 (TTR PlayTicket 패턴) ─────────────────────
  const [, setTick] = useState(0)
  useEffect(() => {
    const unsub = subscribeRummikub(() => setTick(t => t + 1))
    return unsub
  }, [])

  const game              = rummikubStore.getGame()
  const players           = rummikubStore.getPlayers()
  const myHand            = rummikubStore.getMyHand()
  const boardSets         = rummikubStore.getBoardSets()
  const boardTiles        = rummikubStore.getBoardTiles()
  const handSizes         = rummikubStore.getHandSizes()
  const lastTurnResult    = rummikubStore.getLastTurnResult()
  const roundScores       = rummikubStore.getRoundScores()
  const finalScore        = rummikubStore.getFinalScore()

  const isMyTurn = game?.currentTurnUserCode === authState.userCode

  // 본인 user_code 주입 (TTR 패턴)
  useEffect(() => {
    rummikubStore.setMyUserCode(authState.userCode)
  }, [authState.userCode])

  // effectiveMatchId fallback (TTR SEQ=10211 패턴)
  const effectiveMatchId: number | undefined =
    (matchId && matchId > 0)
      ? matchId
      : (game?.matchId && game.matchId > 0 ? game.matchId : undefined)

  // ── Hub 연결 ready 상태 ──────────────────────────────────
  const [hubReady, setHubReady] = useState<boolean>(false)

  // ── Hub 연결 / 해제 + 초기 hydration (TTR PlayTicket 패턴) ─
  useEffect(() => {
    let cancelled = false

    setHubReady(false)
    rmkHub.startConnection(authState.token)
      .then(() => {
        if (cancelled) {
          rmkHub.stopConnection().catch(() => {})
          return
        }
        setHubReady(true)
        // SEQ=10219 catch D fix: roomCode만 있으면 항상 reconnectGame 호출
        // 서버 ReconnectGame이 ResolveMatchId(roomCode)로 matchId 자동 추출 = matchId prop=0이어도 hydrate 가능
        // 자동 복귀 흐름(App.tsx matchId=0)·초기 게임 시작 흐름 양쪽 robust
        rmkHub.reconnectGame(roomCode).catch(e => {
          console.warn('[PlayRummikub] reconnectGame invoke 실패', e)
        })
      })
      .catch(e => {
        console.error('[PlayRummikub] hub 시작 실패', e)
        if (!cancelled) {
          setErrorToast('서버 연결 실패 — 새로고침 후 재시도')
        }
      })

    return () => {
      cancelled = true
      rmkHub.stopConnection().catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.token, roomCode])

  // ── 에러 토스트 ──────────────────────────────────────────
  const [errorToast, setErrorToast] = useState<string | null>(null)
  useEffect(() => {
    const unsub = rmkHub.onActionError(p => {
      const msg = p?.errorMessage ?? p?.errorCode ?? 'Unknown error'
      setErrorToast(msg)
      window.setTimeout(() => setErrorToast(null), 3500)
    })
    return unsub
  }, [])

  // ── 턴 결과 토스트 (TurnEnded broadcast) ─────────────────
  useEffect(() => {
    if (!lastTurnResult) return
    const { result, reason } = lastTurnResult
    if (result === 'INVALID') {
      setErrorToast(`배치 무효 — ${reason} (패널티 1장)`)
      window.setTimeout(() => {
        setErrorToast(null)
        rummikubStore.clearLastTurnResult()
      }, 3000)
    } else {
      rummikubStore.clearLastTurnResult()
    }
  }, [lastTurnResult])

  // ── 모달 상태 ─────────────────────────────────────────────
  const [showRoundEnd, setShowRoundEnd] = useState<boolean>(false)
  const [showGameEnd,  setShowGameEnd]  = useState<boolean>(false)

  // RoundEnded → 모달 (roundScores 변경 감지)
  const prevRoundLen = useRef<number>(0)
  useEffect(() => {
    if (roundScores.length > prevRoundLen.current) {
      prevRoundLen.current = roundScores.length
      // GameEnded가 따라오면 showGameEnd가 우선
      if (!finalScore) setShowRoundEnd(true)
    }
  }, [roundScores, finalScore])

  // GameEnded → 모달
  useEffect(() => {
    if (finalScore) {
      setShowRoundEnd(false)
      setShowGameEnd(true)
    }
  }, [finalScore])

  // ── 드래그 추적 (손패 → 보드 PlaceTile) ─────────────────
  const dragTileIdRef = useRef<number>(0)

  const handleHandDragStart = useCallback((tileId: number) => {
    dragTileIdRef.current = tileId
  }, [])

  const handleDropToBoard = useCallback(
    (_tileId: number, targetSetId: number, position: number) => {
      const tileId = dragTileIdRef.current
      if (!tileId || !isMyTurn || !hubReady) return
      // SEQ=10222 catch 3 fix: 첫 진입(30점) 미완료 상태일 때만 자동 세트 통합 허용
      // 첫 진입 완료 후에는 매 PLACE를 신규 set(targetSetId=0 그대로)으로 보내 룰 set 분리 보장
      const myFirstMeldDone = rummikubStore.getFirstMeldStatus().get(authState.userCode) ?? false
      const actualSetId = !myFirstMeldDone && targetSetId === 0 && rmkHub.getLastPlacedSetId() > 0
        ? rmkHub.getLastPlacedSetId()
        : targetSetId
      rmkHub.placeTile(roomCode, tileId, actualSetId, position)
        .catch(e => console.error('[PlayRummikub] placeTile', e))
      dragTileIdRef.current = 0
    },
    [isMyTurn, hubReady, roomCode],
  )

  // 보드 → 보드 이동 (MoveTile)
  const handleMoveTile = useCallback(
    (tileId: number, fromSetId: number, toSetId: number, position: number) => {
      if (!isMyTurn || !hubReady) return
      rmkHub.moveTile(roomCode, tileId, fromSetId, toSetId, position)
        .catch(e => console.error('[PlayRummikub] moveTile', e))
    },
    [isMyTurn, hubReady, roomCode],
  )

  // ── Hub guardHub ─────────────────────────────────────────
  function guardHub(): boolean {
    if (!hubReady) {
      setErrorToast('서버 연결 중... 잠시 후 다시 시도해주세요')
      window.setTimeout(() => setErrorToast(null), 2500)
      return false
    }
    return true
  }

  // ── 액션 핸들러 ─────────────────────────────────────────
  function handleDraw() {
    if (!isMyTurn || !guardHub()) return
    rmkHub.drawTile(roomCode)
      .catch(e => console.error('[PlayRummikub] drawTile', e))
  }

  function handleEndTurn() {
    if (!isMyTurn || !guardHub()) return
    rmkHub.endTurn(roomCode)
      .catch(e => console.error('[PlayRummikub] endTurn', e))
  }

  function handleUndo() {
    if (!isMyTurn || !guardHub()) return
    rmkHub.undo(roomCode)
      .catch(e => console.error('[PlayRummikub] undo', e))
  }

  async function handleLeaveGame() {
    if (!window.confirm(
      '게임을 나가시겠습니까?\n진행 중인 게임은 계속됩니다.',
    )) return
    rummikubStore.clearGame()
    await rmkHub.stopConnection().catch(() => {})
    onLeave()
  }

  // ── 반응형 Stage 크기 ─────────────────────────────────────
  const { width: winW, height: winH } = useWindowSize()

  // 모바일 감지 (<768px)
  const isMobile = winW < 768

  // Stage 크기: 전체 화면 - 상단 헤더(48px) - 액션패널(모바일: 하단 120px)
  const headerH      = 48
  const actionPanelH = isMobile ? 120 : 0
  const stageWidth   = winW
  const stageHeight  = winH - headerH - actionPanelH

  // 로딩 가드
  if (!game || players.length === 0) {
    return (
      <div className="rmk-play-loading">
        <p>루미큐브 — 게임 로딩 중...</p>
        <small>방 {roomCode} · 매치 {effectiveMatchId ?? 0}</small>
        <div>
          <button onClick={onLeave}>나가기</button>
          <button onClick={onLogout}>로그아웃</button>
        </div>
      </div>
    )
  }

  const latestRoundScore = roundScores.length > 0 ? roundScores[roundScores.length - 1] : undefined

  return (
    <div className="rmk-play" data-my-turn={isMyTurn ? '1' : '0'}>

      {/* ── 상단 헤더 ── */}
      <header className="rmk-header" style={{ height: headerH }}>
        <span>루미큐브</span>
        <span>방 {roomCode}</span>
        <span data-mobile-hide="1">매치 {effectiveMatchId ?? 0}</span>
        <span>턴 {game.turnNo} · {game.gamePhase}</span>
        <span className={isMyTurn ? 'rmk-header-myturn-on' : 'rmk-header-myturn-off'}>
          {isMyTurn ? '내 턴' : '대기'}
        </span>
        {!hubReady && (
          <span className="rmk-header-connecting" title="서버 연결 중">연결 중</span>
        )}
        <span data-mobile-hide="1">
          {game.roundMode === 'CUMULATIVE'
            ? `${game.currentRoundNo}/${game.totalRounds} 라운드`
            : '단일 라운드'
          }
        </span>
        <button onClick={handleLeaveGame}>나가기</button>
        <button onClick={onLogout}>로그아웃</button>
      </header>

      {/* ── PC/태블릿 레이아웃: 좌(플레이어) + 중(Konva Stage) + 우(액션패널) ── */}
      <div
        className="rmk-play-body"
        style={{
          display: 'flex',
          height: stageHeight + actionPanelH,
        }}
      >
        {/* 좌: 플레이어 패널 (데스크톱만) */}
        {!isMobile && (
          <aside className="rmk-left-pane" style={{ width: 160, flexShrink: 0 }}>
            <div className="rmk-players-panel">
              <h4>플레이어</h4>
              {players.map(p => {
                const hSize = p.userCode === authState.userCode
                  ? myHand.length
                  : (handSizes.get(p.userCode) ?? 0)
                const isActive = game.currentTurnUserCode === p.userCode
                return (
                  <div
                    key={p.userCode}
                    className={'rmk-player-row' + (isActive ? ' rmk-player-active' : '')}
                  >
                    <span className="rmk-player-nick">{p.nickname}</span>
                    <span className="rmk-player-hand">{hSize}장</span>
                    {p.firstMeldCompleted && (
                      <span className="rmk-player-meld" title="첫 진입 완료">✓</span>
                    )}
                    {p.connectionState !== 'CONNECTED' && (
                      <span className="rmk-player-dc" title={p.connectionState}>!</span>
                    )}
                  </div>
                )
              })}

              {/* 누적 점수 (CUMULATIVE 모드) */}
              {game.roundMode === 'CUMULATIVE' && (
                <div className="rmk-cumulative-scores">
                  <h4>누적 점수</h4>
                  {players.map(p => (
                    <div key={p.userCode} className="rmk-score-row">
                      <span>{p.nickname}</span>
                      <span>{rummikubStore.getCumulativeScores().get(p.userCode) ?? 0}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        )}

        {/* 중: Konva Stage (3 Layer) */}
        <main
          className="rmk-stage-wrap"
          style={{ flex: 1, overflow: 'hidden', position: 'relative' }}
        >
          <Stage
            width={stageWidth - (isMobile ? 0 : 160 + 200)}
            height={stageHeight}
          >
            {/* Layer 1: BoardLayer — 보드 세트·타일 (TD §3.2 중앙 영역) */}
            <RmkBoard
              stageWidth={stageWidth - (isMobile ? 0 : 160 + 200)}
              stageHeight={stageHeight}
              boardSets={boardSets}
              boardTiles={boardTiles}
              isMyTurn={isMyTurn}
              myUserCode={authState.userCode}
              onMoveTile={handleMoveTile}
              onDropFromHand={(tileId, targetSetId, position) => {
                handleDropToBoard(tileId, targetSetId, position)
              }}
            />

            {/* Layer 2: HandLayer — 손패 (본인 face, 타인 장수) */}
            <RmkHand
              stageWidth={stageWidth - (isMobile ? 0 : 160 + 200)}
              stageHeight={stageHeight}
              myHand={myHand}
              players={players}
              handSizes={handSizes}
              myUserCode={authState.userCode}
              isMyTurn={isMyTurn}
              onDragStart={handleHandDragStart}
              onDropToBoard={handleDropToBoard}
            />

            {/* Layer 3: UILayer — 플레이어 리스트 오버레이 (모바일) */}
            {isMobile && (
              <Layer>
                {/* 모바일: 우상단 플레이어 축약 표시 */}
                {players.map((p, idx) => {
                  const hSize = p.userCode === authState.userCode
                    ? myHand.length
                    : (handSizes.get(p.userCode) ?? 0)
                  const isActive = game.currentTurnUserCode === p.userCode
                  return (
                    <React.Fragment key={p.userCode}>
                      <Rect
                        x={(stageWidth - (isMobile ? 0 : 360)) - 110}
                        y={8 + idx * 28}
                        width={100}
                        height={24}
                        fill={isActive ? 'rgba(25,118,210,0.8)' : 'rgba(0,0,0,0.5)'}
                        cornerRadius={4}
                      />
                      <Text
                        x={(stageWidth - (isMobile ? 0 : 360)) - 108}
                        y={13 + idx * 28}
                        text={`${p.nickname} ${hSize}장`}
                        fontSize={11}
                        fill="#fff"
                      />
                    </React.Fragment>
                  )
                })}

                {/* 턴 타이머 (상단 중앙) */}
                {isMyTurn && game.turnDeadlineTs > 0 && (() => {
                  const sLeft = Math.max(0, Math.floor((game.turnDeadlineTs - Date.now()) / 1000))
                  const cx = (stageWidth - (isMobile ? 0 : 360)) / 2
                  return (
                    <>
                      <Circle
                        x={cx}
                        y={24}
                        radius={22}
                        fill={sLeft <= 15 ? '#F44336' : sLeft <= 30 ? '#FF9800' : '#4CAF50'}
                        opacity={0.9}
                      />
                      <Text
                        x={cx - 22}
                        y={15}
                        width={44}
                        text={String(sLeft)}
                        fontSize={14}
                        fontStyle="bold"
                        fill="#fff"
                        align="center"
                      />
                    </>
                  )
                })()}
              </Layer>
            )}
          </Stage>
        </main>

        {/* 우: 액션 패널 (데스크톱만 — 모바일은 하단 fixed) */}
        {!isMobile && (
          <aside className="rmk-right-pane" style={{ width: 200, flexShrink: 0 }}>
            <RmkActionPanel
              isMyTurn={isMyTurn}
              hubReady={hubReady}
              turnDeadlineTs={game.turnDeadlineTs}
              drawPileRemaining={game.drawPileRemaining}
              myHandSize={myHand.length}
              onDraw={handleDraw}
              onEndTurn={handleEndTurn}
              onUndo={handleUndo}
              onDisabledReason={reason => {
                setErrorToast(reason)
                window.setTimeout(() => setErrorToast(null), 2500)
              }}
            />
          </aside>
        )}
      </div>

      {/* 모바일 하단 액션 패널 */}
      {isMobile && (
        <div
          className="rmk-mobile-action"
          style={{ height: actionPanelH, position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100 }}
        >
          <RmkActionPanel
            isMyTurn={isMyTurn}
            hubReady={hubReady}
            turnDeadlineTs={game.turnDeadlineTs}
            drawPileRemaining={game.drawPileRemaining}
            myHandSize={myHand.length}
            onDraw={handleDraw}
            onEndTurn={handleEndTurn}
            onUndo={handleUndo}
            onDisabledReason={reason => {
              setErrorToast(reason)
              window.setTimeout(() => setErrorToast(null), 2500)
            }}
          />
        </div>
      )}

      {/* 라운드 종료 모달 */}
      {showRoundEnd && latestRoundScore && !showGameEnd && (
        <RmkGameEndModal
          roundScore={latestRoundScore}
          players={players}
          myUserCode={authState.userCode}
          onNextRound={() => setShowRoundEnd(false)}
          onClose={() => setShowRoundEnd(false)}
        />
      )}

      {/* 게임 종료 모달 */}
      {showGameEnd && finalScore && (
        <RmkGameEndModal
          roundScore={latestRoundScore}
          finalScore={finalScore}
          players={players}
          myUserCode={authState.userCode}
          onClose={() => {
            rummikubStore.clearGame()
            onLeave()
          }}
        />
      )}

      {/* 에러 토스트 */}
      {errorToast && (
        <div
          className="rmk-error-toast"
          onClick={() => setErrorToast(null)}
          style={{
            position: 'fixed',
            bottom: isMobile ? actionPanelH + 8 : 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(211,47,47,0.95)',
            color: '#fff',
            padding: '8px 20px',
            borderRadius: 6,
            zIndex: 200,
            cursor: 'pointer',
            maxWidth: '90vw',
            textAlign: 'center',
            fontSize: 14,
          }}
        >
          {errorToast}
        </div>
      )}
    </div>
  )
}

export default PlayRummikub
